import React, { useState } from "react";
import { Facebook, Twitter, Linkedin, MessageCircle, Link2, Check, Share2 } from "lucide-react";

/**
 * ShareBar — Boutons de partage social (Facebook, Twitter/X, WhatsApp, LinkedIn) + copie du lien.
 * Props:
 *  - url: URL à partager (défaut = window.location.href)
 *  - title: titre à partager
 *  - text: texte plus long (WhatsApp, email)
 *  - variant: "light" (fond clair) ou "dark" (fond foncé)
 *  - label: texte du sous-titre (par ex. "Partagez KWIKILA Care")
 */
export default function ShareBar({
  url,
  title = "KWIKILA Care — Plateforme santé numérique pour les hôpitaux en RDC",
  text = "Découvrez KWIKILA Care : gestion patients, rappels SMS automatiques, conseils IA en 5 langues locales, rapports Ministère de la Santé.",
  variant = "light",
  label = "Partagez KWIKILA Care",
}) {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.origin + "/" : "https://kwikilacare.com/");
  const [copied, setCopied] = useState(false);

  const enc = encodeURIComponent;
  const links = [
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(text)}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      color: "#25D366",
      href: `https://api.whatsapp.com/send?text=${enc(text + "\n\n" + shareUrl)}`,
    },
    {
      key: "twitter",
      label: "X / Twitter",
      icon: Twitter,
      color: "#000000",
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}&hashtags=SantéRDC,KWIKILACare,Congo`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      color: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback: prompt
      window.prompt("Copiez le lien ci-dessous :", shareUrl);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text, url: shareUrl }); return; }
      catch { /* user cancelled */ }
    }
    copyLink();
  };

  const isDark = variant === "dark";
  const textColor = isDark ? "text-white/80" : "text-[#5C6B61]";
  const btnBase = isDark
    ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
    : "bg-white hover:bg-[#FDFBF7] text-[#1A1D1A] border border-[#E5EBE7]";

  return (
    <div data-testid="share-bar" className="flex flex-col items-center gap-3">
      <div className={`text-xs font-semibold uppercase tracking-widest ${textColor}`}>{label}</div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {links.map(({ key, label: lb, icon: Icon, color, href }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${key}`}
            aria-label={`Partager sur ${lb}`}
            className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-105 hover:shadow-md ${btnBase}`}
          >
            <Icon
              className="w-4 h-4 transition-colors"
              style={{ color }}
              strokeWidth={2}
            />
            <span className="hidden sm:inline">{lb}</span>
          </a>
        ))}
        <button
          onClick={copyLink}
          data-testid="share-copy"
          aria-label="Copier le lien"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-105 hover:shadow-md ${btnBase}`}
        >
          {copied ? <><Check className="w-4 h-4 text-[#2E7D32]"/> <span className="hidden sm:inline">Copié !</span></>
                  : <><Link2 className="w-4 h-4"/> <span className="hidden sm:inline">Copier</span></>}
        </button>
        <button
          onClick={nativeShare}
          data-testid="share-native"
          aria-label="Autres options de partage"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-105 hover:shadow-md sm:hidden ${btnBase}`}
        >
          <Share2 className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}
